// File: LandingPage/Sections/HowItWorksSection.jsx
'use client'
import { CheckCircleIcon as StepNumberCheckIcon } from '@heroicons/react/24/solid' // For the step number

// Icons for each step - choosing one primary icon
import { ArrowUpTrayIcon } from '@heroicons/react/24/outline'; // Step 1
import { CheckBadgeIcon } from '@heroicons/react/24/solid';   // Step 2
import { SparklesIcon as SparklesSolidIcon } from '@heroicons/react/24/solid'; // Step 3
import { getWhatsAppUrl } from '../../../../config/contact';


const steps = [
  {
    id: 1,
    name: "Send Us Your Menu",
    description: "Snap photos of your menu and send them via WhatsApp, or let us visit to scan them professionally. Quick and easy!",
    IconComponent: ArrowUpTrayIcon,
    iconColor: "text-primary-500", // Color for the main icon
    iconBgColor: "bg-primary-100", // Background for the icon container
    stepBadgeColor: "text-primary-500", // Color for the step number badge
    stepBadgeBgColor: "bg-primary-50",
  },
  {
    id: 2,
    name: "AI Digitises, Humans Verify",
    description: "Our smart AI extracts all dishes and prices, then our team meticulously verifies every detail for 100% accuracy.",
    IconComponent: CheckBadgeIcon,
    iconColor: "text-accent-600",
    iconBgColor: "bg-accent-100",
    stepBadgeColor: "text-accent-500",
    stepBadgeBgColor: "bg-accent-50",
  },
  {
    id: 3,
    name: "Your Menu Goes Live!",
    description: "Receive your stunning digital menu and QR codes. Customers scan, browse, and enjoy. Updates take just minutes!",
    IconComponent: SparklesSolidIcon,
    iconColor: "text-highlight-500",
    iconBgColor: "bg-highlight-100",
    stepBadgeColor: "text-highlight-500",
    stepBadgeBgColor: "bg-highlight-50",
  }
];

export default function HowItWorksSection() {
  return (
    <div id="how-it-works" className="py-28 sm:py-36 bg-background-50 relative overflow-hidden">
      <div className="absolute -top-32 -left-32 w-96 h-96 bg-primary-100/40 rounded-full blur-3xl opacity-50 animate-pulse"></div>
      <div className="absolute -bottom-32 -right-32 w-96 h-96 bg-accent-100/40 rounded-full blur-3xl opacity-50 animate-pulse animation-delay-3000"></div>

      <div className="mx-auto max-w-7xl px-6 lg:px-8 relative z-10">
        <div className="mb-20 text-center">
          <h2 className="text-lg font-semibold text-primary-500 tracking-wider uppercase">
            Simple Process
          </h2>
          <p className="mt-3 text-4xl font-extrabold tracking-tight text-text-700 sm:text-5xl lg:text-6xl">
            How <span className="text-transparent bg-clip-text bg-gradient-to-r from-accent-500 to-highlight-400">ScanMaadi</span> Works
          </p>
          <p className="mt-6 max-w-3xl text-xl text-text-500 mx-auto leading-relaxed">
            Transform your paper menu into a dynamic, digital delight in just three straightforward steps.
          </p>
        </div>

        <div className="max-w-4xl mx-auto">
          {steps.map((step, index) => (
            <div 
              key={step.id} 
              className={`relative flex flex-col md:flex-row items-center group 
                          ${index % 2 === 1 ? 'md:flex-row-reverse' : ''} 
                          ${index < steps.length - 1 ? 'mb-16 md:mb-24 lg:mb-28' : ''}`}
            >
              {/* Icon Side */}
              <div className={`w-full md:w-2/5 flex justify-center md:justify-start ${index % 2 === 1 ? 'md:justify-end' : 'md:justify-start'} mb-8 md:mb-0`}>
                <div className={`relative p-6 rounded-full shadow-xl ${step.iconBgColor} group-hover:scale-105 interactive-transition`}>
                  <step.IconComponent 
                    className={`h-20 w-20 sm:h-24 sm:w-24 lg:h-28 lg:w-28 ${step.iconColor} transition-transform duration-300 group-hover:rotate-[-5deg]`} 
                    strokeWidth={step.IconComponent === ArrowUpTrayIcon ? 1.5 : undefined} // Thinner stroke for outline icons if ArrowUpTrayIcon
                  />
                   <div className={`absolute -top-4 -left-4 flex items-center justify-center h-16 w-16 rounded-full ${step.stepBadgeBgColor} shadow-lg ring-4 ring-white/80`}>
                      <span className={`text-2xl sm:text-3xl font-bold ${step.stepBadgeColor}`}>{step.id}</span>
                      <StepNumberCheckIcon className={`absolute -top-1 -right-1 h-6 w-6 ${step.stepBadgeColor} bg-white rounded-full p-px shadow`} />
                    </div>
                </div>
              </div>

              {/* Text Content Side */}
              <div className={`w-full md:w-3/5 text-center md:text-left ${index % 2 === 1 ? 'md:pr-8 lg:pr-12' : 'md:pl-8 lg:pl-12'}`}>
                <h3 className="text-2xl lg:text-3xl font-bold text-text-700 mb-4 group-hover:text-primary-600 interactive-transition">
                  {step.name}
                </h3>
                <p className="text-lg text-text-500 leading-relaxed text-pretty">
                  {step.description}
                </p>
              </div>
              
            </div>
          ))}
        </div>

        <div className="mt-24 text-center relative z-10">
          <a
            href={getWhatsAppUrl()}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center justify-center px-10 py-4 rounded-xl bg-gradient-to-r from-accent-500 to-accent-600 shadow-xl transform transition-all duration-500 hover:scale-105 hover:shadow-accent-500/70 text-white font-bold text-lg group"
          >
            Get Your Digital Menu Now
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-6 h-6 ml-2.5 transform group-hover:translate-x-1 interactive-transition">
              <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5 21 12m0 0-7.5 7.5M21 12H3" />
            </svg>
          </a>
        </div>
      </div>
       <div className="absolute bottom-0 left-0 right-0 h-20 bg-gradient-to-t from-white to-transparent z-0 section-divider-bottom opacity-70" style={{clipPath: 'ellipse(100% 50% at 50% 100%)'}}></div>
    </div>
  )
}